/* =====================================================================
   Browns Digital Studio — dashboard theme presets, apply & account sync
===================================================================== */
"use strict";

const BD_THEME_STORAGE = "bd_theme_prefs";

const BD_THEME_VAR_KEYS = [
  "bg","bg-soft","bg-alt","panel","card","field",
  "ink","muted","subtle","line","line-soft",
  "brand","brand-mid","brand-hover","cta","bad"
];

const BD_LIGHT_BASE = {
  bg:"#f5f5f7", "bg-soft":"#f2f2f2", "bg-alt":"#ffffff",
  panel:"#ffffff", card:"#ffffff", field:"#f2f2f2",
  ink:"#1d1d1f", muted:"#6e6e73", subtle:"rgba(60,60,67,0.40)",
  line:"rgba(60,60,67,0.22)", "line-soft":"rgba(60,60,67,0.16)",
  brand:"#355cff", "brand-mid":"#4a6fff", "brand-hover":"#2a4ae0", cta:"#355cff", bad:"#ff3b30",
  good:"#34c759", warn:"#ff9500"
};

/* Meridian dark — matches platform/css/bd-tokens.css data-bd-theme="dark" (one dark palette).
   brand-mid is the dark link tint, #5b8cff (same as --color-tint on dark). */
const BD_DARK_BASE = {
  bg:"#0a0a0a", "bg-soft":"#111111", "bg-alt":"#1a1a1a",
  panel:"#0f0f0f", card:"#0f0f0f", field:"#1f1f1f",
  ink:"#f5f5f5", muted:"#a3a3a3", subtle:"#737373",
  line:"#2e2e2e", "line-soft":"rgba(46,46,46,0.65)",
  brand:"#355cff", "brand-mid":"#5b8cff", "brand-hover":"#2a4ae0", cta:"#355cff", bad:"#ef4444",
  good:"#22c55e", warn:"#f59e0b"
};

const BD_BLUEPRINT_LIGHT = Object.assign({}, BD_LIGHT_BASE);

/* ---- accent-washed light backgrounds -------------------------------------
   Each theme tints the page + grouped surfaces with a whisper of its accent
   so the whole dashboard reads in that colour family, Apple "tinted desktop"
   style. Card/panel surfaces stay near-white so content keeps its elevation,
   and all text/borders stay neutral for legibility. */
function bdMixHex(a, b, t){
  const pa = /^#?([0-9a-f]{6})$/i.exec(String(a||"").replace("#",""));
  const pb = /^#?([0-9a-f]{6})$/i.exec(String(b||"").replace("#",""));
  if (!pa || !pb) return a;
  const ca = pa[1], cb = pb[1];
  const mix = i => {
    const va = parseInt(ca.slice(i,i+2),16), vb = parseInt(cb.slice(i,i+2),16);
    return Math.round(va + (vb - va) * t).toString(16).padStart(2,"0");
  };
  return "#" + mix(0) + mix(2) + mix(4);
}

/* strength 0 = neutral (Blueprint), ~1 = standard tint depth */
function bdTintedLight(accent, strength){
  const s = strength == null ? 1 : strength;
  const wash = (base, amt) => bdMixHex(base, accent, amt * s);
  return {
    bg:        wash("#f4f5f7", 0.060),
    "bg-soft": wash("#eceef1", 0.075),
    "bg-alt":  wash("#ffffff", 0.022),
    panel:     wash("#ffffff", 0.018),
    card:      wash("#ffffff", 0.022),
    field:     wash("#f0f1f4", 0.055),
    ink:"#1d1d1f",
    muted:"#6e6e73",
    subtle:"rgba(60,60,67,0.40)",
    line:        bdMixHex("#c9cace", accent, 0.10 * s),
    "line-soft": bdMixHex("#d8d9dd", accent, 0.10 * s),
    brand: accent, "brand-mid": bdMixHex(accent, "#ffffff", 0.18),
    "brand-hover": bdMixHex(accent, "#000000", 0.16), cta: accent,
    bad:"#ff3b30", good:"#34c759", warn:"#ff9500"
  };
}

/* Legacy multi-theme IDs → Light or Dark only */
const BD_PRESET_ALIASES = {
  studio:"blueprint", meridian:"blueprint", prism:"blueprint",
  surf:"blueprint", kelp:"blueprint", amber:"blueprint",
  northgate:"blueprint", afterglow:"blueprint", tidemark:"blueprint",
  understory:"blueprint", emberline:"blueprint", pearl:"blueprint", sunset:"blueprint",
  aurora:"midnight", carbon:"midnight", neon:"midnight",
  light:"blueprint", dark:"midnight",
  "default-light":"blueprint", "default-dark":"midnight"
};

function bdThemeAccent(base, brand, brandMid, brandHover, cta){
  return Object.assign({}, base, { brand, "brand-mid": brandMid, "brand-hover": brandHover, cta });
}

/* preset = name + tag + accent + tint strength. Light only — every theme is a
   light theme that washes the background with its own accent. */
function bdMakePreset(name, tag, accent, strength){
  const light = bdTintedLight(accent, strength);
  return { name, tag, light, dark: light, meshLight:"none", meshDark:"none" };
}

/* Two built-in themes: Light + Dark (Meridian). Preset id "midnight" is kept for
   stored prefs; values match bd-tokens data-bd-theme="dark" (no navy / no blue mesh). */
const BD_THEME_PRESETS = {
  blueprint: Object.assign(
    bdMakePreset("Light", "Browns Digital", "#355cff", 0.22),
    { light: Object.assign({}, BD_BLUEPRINT_LIGHT, {
        bg:"#f5f6f8", "bg-soft":"#eef0f3", "bg-alt":"#ffffff", panel:"#ffffff", card:"#ffffff",
        field:"#f1f2f5", brand:"#355cff", "brand-mid":"#5b8cff", "brand-hover":"#2a4ae0", cta:"#355cff" }) }
  ),
  midnight: {
    name: "Dark",
    tag: "Studio",
    mode: "dark",
    light: Object.assign({}, BD_DARK_BASE),
    dark: Object.assign({}, BD_DARK_BASE),
    meshLight: "none",
    meshDark: "none"
  }
};
/* light presets mirror light into dark so any lookup is safe */
Object.keys(BD_THEME_PRESETS).forEach(k => {
  const p = BD_THEME_PRESETS[k];
  if (!p.mode) p.mode = "light";
  if (p.mode === "light") p.dark = p.light;
});

const BD_TOKEN_LABELS = {
  bg:"Page background",
  "bg-soft":"Soft background",
  "bg-alt":"Alt background",
  panel:"Top bar & sidebar",
  card:"Main surface",
  field:"Input fields",
  ink:"Primary text",
  muted:"Secondary text",
  subtle:"Muted text",
  line:"Borders",
  "line-soft":"Soft borders",
  brand:"Brand accent",
  "brand-mid":"Brand mid",
  "brand-hover":"Brand hover",
  cta:"Primary action",
  bad:"Destructive actions"
};

function bdNormalizePresetId(id){
  if (BD_THEME_PRESETS[id]) return id;
  if (BD_PRESET_ALIASES[id]) return BD_PRESET_ALIASES[id];
  return "blueprint";
}

function bdDefaultThemePrefs(){
  /* LIGHT is the default for everyone (Studio, Staff, Marketing, Help).
     Dark is only applied when user explicitly picks it via theme prefs/storage.
     Do NOT auto-switch based on hostname or prefers-color-scheme. */
  return { preset: "blueprint", mode: "light", custom: null };
}

/* ---- theme storage layers ------------------------------------------------
   Source of truth for signed-in accounts: profiles.theme_prefs on the server
   (https://brownsdigital.org/platform/api/theme.php).
   localStorage + a first-party cookie are ONLY a flash-prevention paint cache
   so the UI does not blink while the hosted prefs load. They must never win
   over a successful server read, and saves must succeed on the server. */
const BD_THEME_COOKIE = "bd_theme_prefs";
const BD_THEME_API = (typeof location !== "undefined" && /(^|\.)brownsdigital\.org$/i.test(location.hostname))
  ? "https://brownsdigital.org/platform/api/theme.php"
  : "/platform/api/theme.php";

function bdThemeCookieDomain(){
  const h = (typeof location !== "undefined" && location.hostname) || "";
  return /(^|\.)brownsdigital\.org$/i.test(h) ? ".brownsdigital.org" : "";
}

function bdThemeCookieWrite(prefs){
  try {
    const payload = encodeURIComponent(JSON.stringify({
      preset: prefs.preset, mode: prefs.mode, custom: prefs.custom || null,
      t: Date.now()
    }));
    if (payload.length > 3500) return; /* keep well under the 4KB cookie cap */
    const domain = bdThemeCookieDomain();
    let c = BD_THEME_COOKIE + "=" + payload + "; path=/; max-age=31536000; SameSite=Lax";
    if (domain) c += "; domain=" + domain + "; Secure";
    document.cookie = c;
  } catch {}
}

function bdThemeCookieRead(){
  try {
    const m = document.cookie.match(new RegExp("(?:^|;\\s*)" + BD_THEME_COOKIE + "=([^;]+)"));
    if (!m) return null;
    const p = JSON.parse(decodeURIComponent(m[1]));
    if (!p || typeof p !== "object" || !p.preset) return null;
    return p;
  } catch {
    return null;
  }
}

/** Paint-cache only — never treat this as authoritative for signed-in users. */
function bdLoadThemePrefs(){
  try {
    const cookie = bdThemeCookieRead();
    const raw = localStorage.getItem(BD_THEME_STORAGE);
    if (cookie) {
      let local = null;
      try { local = raw ? JSON.parse(raw) : null; } catch {}
      const localT = (local && Number(local.t)) || 0;
      const cookieT = Number(cookie.t) || 0;
      if (!local || cookieT >= localT) {
        const legacyMode = bdMapLegacyThemeMode(cookie.mode);
        const p = {
          preset: bdNormalizePresetId(cookie.preset),
          mode: "light",
          custom: (cookie.custom && typeof cookie.custom === "object") ? cookie.custom : null,
          t: cookieT
        };
        p.mode = (cookie.mode === "default-dark" || cookie.mode === "default-light")
          ? legacyMode
          : (BD_THEME_PRESETS[p.preset]?.mode || "light");
        try { localStorage.setItem(BD_THEME_STORAGE, JSON.stringify(p)); } catch {}
        return p;
      }
    }
    if (raw) {
      const p = JSON.parse(raw);
      const legacyMode = bdMapLegacyThemeMode(p.mode);
      p.preset = bdNormalizePresetId(p.preset);
      p.mode = (legacyMode === "dark" || legacyMode === "light") && (p.mode === "default-dark" || p.mode === "default-light")
        ? legacyMode
        : (BD_THEME_PRESETS[p.preset]?.mode || "light");
      if (p.custom && typeof p.custom !== "object") p.custom = null;
      if (p.preset === "blueprint" && p.custom) {
        if (
          p.custom.brand === "#355cff" || p.custom.brand === "#0071e3" ||
          p.custom.cta === "#355cff" || p.custom.cta === "#0071e3"
        ) {
          delete p.custom.brand;
          delete p.custom.cta;
          delete p.custom["brand-mid"];
          delete p.custom["brand-hover"];
          if (!Object.keys(p.custom).length) p.custom = null;
        }
      }
      return p;
    }
    const legacy = localStorage.getItem("bd_theme");
    if (legacy === "default-dark") return { preset:"midnight", mode:"dark", custom:null };
    if (legacy === "default-light") return { preset:"blueprint", mode:"light", custom:null };
    if (legacy === "dark" || legacy === "light") return { preset:"blueprint", mode:"light", custom:null };
    return { preset:"blueprint", mode:"light", custom:null };
  } catch {
    return { preset:"blueprint", mode:"light", custom:null };
  }
}

/** Write flash-prevention cache after a hosted apply/save. */
function bdCacheThemePrefs(prefs){
  prefs.preset = bdNormalizePresetId(prefs.preset);
  prefs.t = Date.now();
  try {
    localStorage.setItem(BD_THEME_STORAGE, JSON.stringify(prefs));
    localStorage.setItem("bd_theme", prefs.mode);
  } catch {}
  bdThemeCookieWrite(prefs);
}

function bdSaveThemePrefs(prefs){
  bdCacheThemePrefs(prefs);
}

function bdPresetTokens(presetId, mode){
  const preset = BD_THEME_PRESETS[bdNormalizePresetId(presetId)];
  const tokens = Object.assign({}, preset[mode === "dark" ? "dark" : "light"]);
  tokens.mesh = preset[mode === "dark" ? "meshDark" : "meshLight"];
  return tokens;
}

function bdResolveThemeTokens(prefs){
  const base = bdPresetTokens(prefs.preset, prefs.mode);
  if (!prefs.custom) return base;
  return Object.assign({}, base, prefs.custom);
}

function bdMapLegacyThemeMode(mode){
  if (mode === "default-dark") return "dark";
  if (mode === "default-light") return "light";
  return mode;
}

function bdHigThemeId(prefs){
  const mode = bdMapLegacyThemeMode(prefs && prefs.mode);
  return mode === "dark" ? "dark" : "light";
}

function bdHexToRgb(hex){
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return "0,122,255";
  return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)].join(",");
}

/* hex (#rrggbb) -> rgba(...) string; null if not a clean 6-digit hex */
function bdRgbaFromHex(hex, alpha){
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0,2),16), g = parseInt(h.slice(2,4),16), b = parseInt(h.slice(4,6),16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/* Every HIG token this layer may override inline. Cleared on each apply so the
   base data-bd-theme block can supply defaults for anything we don't set. */
const BD_HIG_MANAGED = [
  "--color-tint","--color-tint-rgb","--color-tint-hover","--color-tint-pressed",
  "--color-tint-fill","--color-tint-fill-hover","--color-info",
  "--color-bg-primary","--color-bg-secondary","--color-bg-tertiary","--color-bg-grouped","--color-bg-elevated","--color-bg-inset",
  "--color-label-primary","--color-label-secondary","--color-label-tertiary","--color-label-quaternary",
  "--color-separator","--color-separator-opaque",
  "--color-destructive","--color-success","--color-warning"
];

/* Map the resolved theme onto HIG --color-* tokens so studio-hig.css and the
   legacy bridge in bd-tokens.css always agree — every preset, every custom edit. */
function bdApplyHigTokens(tokens, prefs){
  const root = document.documentElement;
  const preset = bdNormalizePresetId(prefs.preset);
  const dark = prefs.mode === "dark";
  root.dataset.bdTheme = bdHigThemeId(prefs);

  BD_HIG_MANAGED.forEach(p => root.style.removeProperty(p));

  const set = (name, val) => { if (val != null && val !== "") root.style.setProperty(name, val); };

  /* CTA fills stay #355cff (white-on-fill AA). Hover darkens; it does not lighten. */
  const fill = "#355cff";
  const fillHover = "#2a4ae0";
  set("--color-tint-fill", fill);
  set("--color-tint-fill-hover", fillHover);

  const brand = tokens.brand || tokens.cta || "#355cff";
  const tint = dark
    ? (String(brand).toLowerCase() === "#355cff" ? "#5b8cff" : bdMixHex(brand, "#ffffff", 0.22))
    : brand;
  set("--color-tint", tint);
  set("--color-tint-rgb", bdHexToRgb(tint));
  /* Hover darkens. Default brand uses #2a4ae0 / pressed #1e40af (white text AA). */
  const brandKey = String(brand).toLowerCase();
  set("--color-tint-hover", brandKey === "#355cff"
    ? (dark ? "#355cff" : "#2a4ae0")
    : bdMixHex(tint, "#000000", dark ? 0.12 : 0.16));
  set("--color-tint-pressed", brandKey === "#355cff" ? "#1e40af" : bdMixHex(brand, "#000000", 0.28));
  set("--color-info", tint);

  set("--color-bg-primary",   tokens.bg);
  set("--color-bg-secondary", tokens["bg-soft"]);
  set("--color-bg-tertiary",  tokens["bg-alt"]);
  set("--color-bg-grouped",   tokens["bg-soft"]);
  set("--color-bg-elevated",  tokens.card || tokens.panel);
  /* Inset panels (mail sidebar, RTE toolbar) must track the theme — never the light-only default. */
  set("--color-bg-inset",     tokens["bg-soft"] || tokens["bg-alt"] || tokens.bg);

  set("--color-label-primary",   tokens.ink);
  set("--color-label-secondary", tokens.muted);
  set("--color-label-tertiary",  tokens.subtle);
  set("--color-label-quaternary", bdRgbaFromHex(tokens.ink, dark ? 0.22 : 0.18));

  set("--color-separator",        tokens["line-soft"]);
  set("--color-separator-opaque", tokens.line);

  set("--color-destructive", tokens.bad);
  set("--color-success",     tokens.good);
  set("--color-warning",     tokens.warn);
}

function bdApplyThemePrefs(prefs){
  bdPaintThemePrefs(prefs);
  bdSaveThemePrefs(prefs);
}

function bdPaintThemePrefs(prefs){
  prefs.mode = bdMapLegacyThemeMode(prefs.mode);
  prefs.preset = bdNormalizePresetId(prefs.preset);
  const tokens = bdResolveThemeTokens(prefs);
  document.documentElement.dataset.theme = prefs.mode;
  document.documentElement.dataset.themePreset = prefs.preset;
  /* Do not write legacy --bg/--brand inline — hig-tokens bridge maps those from
     --color-*. Clearing stale inline values keeps one source of truth. */
  BD_THEME_VAR_KEYS.forEach(k => {
    document.documentElement.style.removeProperty("--" + k);
  });
  if (tokens.mesh) document.documentElement.style.setProperty("--mesh", tokens.mesh);
  else document.documentElement.style.removeProperty("--mesh");
  bdApplyHigTokens(tokens, prefs);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", tokens.bg || "#ffffff");
  document.documentElement.style.removeProperty("--bd-cursor-auto");
  document.documentElement.style.removeProperty("--bd-cursor-pointer");
}

/* Smooth theme transitions after first paint */
let _higThemeReady = false;
function bdEnableThemeTransitions(){
  if (_higThemeReady) return;
  _higThemeReady = true;
  document.documentElement.classList.add("bd-theme-ready");
}

function bdRandomThemePrefs(){
  const ids = Object.keys(BD_THEME_PRESETS);
  const pick = ids[Math.floor(Math.random() * ids.length)];
  return {
    preset: pick,
    mode: BD_THEME_PRESETS[pick].mode || "light",
    custom: null
  };
}

function bdEmptyAccountThemes(){
  return { active:null, saved:[] };
}

function bdNormalizeAccountThemes(raw){
  const base = bdEmptyAccountThemes();
  if (!raw || typeof raw !== "object") return base;
  base.active = raw.active || null;
  base.saved = Array.isArray(raw.saved) ? raw.saved.filter(t => t && t.id && t.name) : [];
  return base;
}

const BD_THEME_NET_KEY = "bd_theme_net_v1";
const BD_THEME_NET_TTL = 5 * 60 * 1000;

function bdThemeNetRead(){
  try {
    const row = JSON.parse(sessionStorage.getItem(BD_THEME_NET_KEY) || "null");
    if (!row || !row.t || !row.v) return null;
    if (Date.now() - row.t > BD_THEME_NET_TTL) return null;
    return row.v;
  } catch { return null; }
}
function bdThemeNetWrite(json){
  try { sessionStorage.setItem(BD_THEME_NET_KEY, JSON.stringify({ t: Date.now(), v: json })); } catch {}
}
function bdThemeNetClear(){
  try { sessionStorage.removeItem(BD_THEME_NET_KEY); } catch {}
}

async function bdPlatformThemeRequest(method, body){
  const isGet = !method || method === "GET";
  if (isGet) {
    const hit = bdThemeNetRead();
    if (hit) return hit;
  }
  const opts = {
    method: method || "GET",
    credentials: "include",
    cache: "default",
    headers: { Accept: "application/json" }
  };
  if (body != null) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await (globalThis.bdFetch || fetch)(BD_THEME_API, opts, 8000);
  if (!res.ok) throw new Error("theme api " + res.status);
  const json = await res.json();
  if (!json || json.ok === false) throw new Error(json?.error || "theme api failed");
  if (isGet) bdThemeNetWrite(json);
  else bdThemeNetClear();
  return json;
}

function bdAccountThemesNonEmpty(acct){
  const a = bdNormalizeAccountThemes(acct);
  return !!(a.active || (a.saved && a.saved.length));
}

async function bdFetchAccountThemes(api){
  /* Hosted SSO API is authoritative across devices. App Supabase clients are
     a secondary mirror and must not hide hosted prefs with an empty {}. */
  let hosted = null;
  let hostedSession = false;
  try {
    const json = await bdPlatformThemeRequest("GET");
    hostedSession = !!json.session;
    if (json.session) hosted = bdNormalizeAccountThemes(json.theme_prefs);
  } catch {}

  if (hostedSession && bdAccountThemesNonEmpty(hosted)) return hosted;

  let appAcct = null;
  if (api?.fetchThemePrefs) {
    try { appAcct = bdNormalizeAccountThemes(await api.fetchThemePrefs()); } catch {}
  }

  if (hostedSession && hosted) return hosted;
  if (bdAccountThemesNonEmpty(appAcct)) return appAcct;
  if (hosted) return hosted;
  return appAcct || bdEmptyAccountThemes();
}

async function bdSyncAccountThemes(api, data){
  const clean = bdNormalizeAccountThemes(data);
  let hostedOk = false;
  let appOk = false;
  try {
    const json = await bdPlatformThemeRequest("PUT", { theme_prefs: clean });
    if (json?.session) hostedOk = true;
  } catch {}
  if (api?.syncThemePrefs) {
    try { await api.syncThemePrefs(clean); appOk = true; } catch {}
  }
  if (!hostedOk && !appOk) {
    throw new Error("Could not save theme to your account.");
  }
  /* Mirror into the paint cache only after a successful hosted/app write. */
  try {
    localStorage.setItem("bd_theme_account", JSON.stringify(clean));
  } catch {}
  return clean;
}

async function bdUpsertActiveAccountTheme(api, prefs, account){
  const snap = bdSnapshotPrefs(prefs);
  let acct = bdNormalizeAccountThemes(account || await bdFetchAccountThemes(api));
  const now = new Date().toISOString();
  if (acct.active){
    const i = acct.saved.findIndex(t => t.id === acct.active);
    if (i >= 0) acct.saved[i] = Object.assign({}, acct.saved[i], snap, { updated_at: now });
    else acct.active = null;
  }
  if (!acct.active){
    const id = crypto.randomUUID();
    acct.saved.unshift(Object.assign({ id, name: "Workspace", updated_at: now }, snap));
    acct.active = id;
  }
  return bdSyncAccountThemes(api, acct);
}

function bdPrefsFromAccountTheme(theme){
  if (!theme) return null;
  return {
    preset: bdNormalizePresetId(theme.preset),
    mode: theme.mode === "dark" ? "dark" : "light",
    custom: theme.custom || null
  };
}

function bdResolveActiveFromAccount(acct){
  const a = bdNormalizeAccountThemes(acct);
  let theme = a.active ? a.saved.find(t => t.id === a.active) : null;
  if (!theme && a.saved.length){
    theme = [...a.saved].sort((x, y) => String(y.updated_at || "").localeCompare(String(x.updated_at || "")))[0];
  }
  return bdPrefsFromAccountTheme(theme);
}

async function bdHydrateThemeFromAccount(api){
  /* Hosted account prefs always win over the local/cookie paint cache. */
  const acct = await bdFetchAccountThemes(api);
  const prefs = bdResolveActiveFromAccount(acct);
  if (!prefs) return null;
  bdApplyThemePrefs(prefs);
  return prefs;
}

/** Marketing / secondary pages: hydrate from the platform SSO theme API (no app client). */
async function bdHydrateThemeFromPlatform(){
  try {
    const json = await bdPlatformThemeRequest("GET");
    if (!json.session) return null;
    const prefs = bdPrefsFromAccountTheme(json.active) || bdResolveActiveFromAccount(json.theme_prefs);
    if (!prefs) return null;
    bdApplyThemePrefs(prefs);
    return prefs;
  } catch {
    return null;
  }
}

/**
 * Boot helper: paint cache instantly, then replace with hosted prefs when signed in.
 * @returns {Promise<object>} final prefs applied
 */
async function bdBootTheme(api){
  const cached = bdLoadThemePrefs();
  bdPaintThemePrefs(cached);
  try {
    const hosted = api
      ? await bdHydrateThemeFromAccount(api)
      : await bdHydrateThemeFromPlatform();
    return hosted || cached;
  } catch {
    return cached;
  }
}

function bdSnapshotPrefs(prefs){
  return {
    preset: bdNormalizePresetId(prefs.preset),
    mode: prefs.mode === "dark" ? "dark" : "light",
    custom: prefs.custom ? JSON.parse(JSON.stringify(prefs.custom)) : null
  };
}

function bdAlignPrefsMode(prefs){
  const p = prefs && typeof prefs === "object"
    ? JSON.parse(JSON.stringify(prefs))
    : bdDefaultThemePrefs();
  p.preset = bdNormalizePresetId(p.preset);
  p.mode = BD_THEME_PRESETS[p.preset]?.mode || "light";
  if (p.custom && typeof p.custom !== "object") p.custom = null;
  return p;
}

function bdCanonicalPrefs(prefs){
  const aligned = bdAlignPrefsMode(prefs);
  const snap = {
    preset: aligned.preset,
    mode: BD_THEME_PRESETS[aligned.preset]?.mode || "light",
    custom: null
  };
  if (aligned.custom && typeof aligned.custom === "object"){
    const base = bdPresetTokens(snap.preset, snap.mode);
    const custom = {};
    Object.keys(aligned.custom).sort().forEach(k => {
      if (!Object.prototype.hasOwnProperty.call(base, k)) return;
      const v = aligned.custom[k];
      const b = base[k];
      if (v == null || b == null) return;
      if (String(v).toLowerCase() !== String(b).toLowerCase()) custom[k] = String(v);
    });
    if (Object.keys(custom).length) snap.custom = custom;
  }
  return snap;
}

function bdPrefsEqual(a, b){
  return JSON.stringify(bdCanonicalPrefs(a)) === JSON.stringify(bdCanonicalPrefs(b));
}

function bdPaintThemePreview(root, tokens, highlightToken){
  if (!root) return;
  const set = (sel, styles) => {
    root.querySelectorAll(sel).forEach(el => Object.assign(el.style, styles));
  };
  root.style.background = tokens.bg;
  set('[data-pr~="bg"]', { background: tokens.bg });
  set('[data-pr~="bg-soft"]', { background: tokens["bg-soft"] });
  set('[data-pr~="bg-alt"]', { background: tokens["bg-alt"] });
  set('[data-pr~="panel"]', { background: tokens.panel, borderColor: tokens["line-soft"] });
  set('[data-pr~="card"]', { background: tokens.card, borderColor: tokens.line });
  set('[data-pr~="field"]', { background: tokens.field, borderColor: tokens.line });
  set('[data-pr~="ink"]', { color: tokens.ink });
  set('[data-pr~="muted"]', { color: tokens.muted });
  set('[data-pr~="subtle"]', { color: tokens.subtle });
  set('[data-pr~="brand"]', { background: tokens.brand });
  set('[data-pr~="brand-mid"]', { background: tokens["brand-mid"], color: "#fff" });
  set('[data-pr~="brand-hover"]', { color: tokens["brand-hover"] });
  set('[data-pr~="cta"]', { background: tokens.cta, color: "#fff" });
  set('[data-pr~="bad"]', { color: tokens.bad });
  set('[data-pr~="line"]', { borderColor: tokens.line });
  set('[data-pr~="line-soft"]', { borderColor: tokens["line-soft"] });
  root.querySelectorAll("[data-pr]").forEach(el => {
    const keys = (el.getAttribute("data-pr") || "").split(/\s+/);
    el.classList.toggle("cust-pr-hl", !!highlightToken && keys.includes(highlightToken));
  });
}

function bdShiftHex(hex, pct){
  const c = /^#([0-9a-f]{6})$/i.exec(hex || ""); if (!c) return hex;
  const n = [0, 2, 4].map(i => parseInt(c[1].slice(i, i + 2), 16));
  const out = n.map(v => Math.max(0, Math.min(255, Math.round(v + (pct / 100) * 255)))).map(v => v.toString(16).padStart(2, "0")).join("");
  return "#" + out;
}
function bdHarmonyColors(brand){
  if (!brand) return [];
  return [brand, bdShiftHex(brand, 18), bdShiftHex(brand, -14), bdShiftHex(brand, 36)].filter((v, i, a) => a.indexOf(v) === i);
}

function bdOpenCustomizeModal(ui){
  const inline = !!ui.mount;
  const toast = ui.toast;
  const esc = ui.esc;
  const lu = ui.lu;
  const icons = ui.icons;
  const api = ui.api;
  const openModalFn = inline
    ? (html) => {
        ui.mount.innerHTML = html.trim();
        const el = ui.mount.querySelector(".cust-modal") || ui.mount.firstElementChild;
        if (el) el.classList.add("cust-inline");
        return el || ui.mount;
      }
    : ui.openModal;
  const closeModalFn = inline ? () => {} : ui.closeModal;
  const setModalCloseHookFn = inline ? () => {} : ui.setModalCloseHook;
  const clearModalCloseHookFn = inline ? () => {} : ui.clearModalCloseHook;
  let draft = bdCanonicalPrefs(bdAlignPrefsMode(bdLoadThemePrefs()));
  let savedPrefs = JSON.parse(JSON.stringify(draft));
  let account = bdEmptyAccountThemes();
  let advanced = false;
  let focusToken = "bg";
  let search = "";

  const m = openModalFn(`<div class="cust-modal${inline ? " cust-inline" : ""}">
    <div class="cust-head">
      <div>${inline ? "" : "<h2>Customize</h2>"}<p class="sub">Themes save to your Browns Digital account and load on every device when you sign in.</p></div>
      ${inline ? "" : `<button type="button" class="icon-btn" id="cust-close" aria-label="Close">${lu("x","lu16")}</button>`}
    </div>
    <div class="cust-body" id="cust-body"></div>
    <div class="cust-foot">
      <div>
        <p class="cust-foot-note">Save theme writes to your hosted account. This device only keeps a temporary paint cache.</p>
        <div class="cust-io-row">
          <button type="button" class="cust-io-btn" id="cust-export">${lu("download","lu14")} Export theme</button>
          <button type="button" class="cust-io-btn" id="cust-import">${lu("upload","lu14")} Import theme</button>
        </div>
      </div>
      <div class="cust-foot-actions">
        <span class="cust-saved" id="cust-saved" hidden aria-live="polite"><i data-lucide="check" class="lu14"></i> Saved</span>
        <button type="button" class="btn btn-dark btn-sm" id="cust-save">Save theme</button>
      </div>
    </div>
    ${inline ? "" : `<div class="cust-exit-prompt" id="cust-exit-prompt" hidden>
      <div class="cust-exit-card" role="alertdialog" aria-modal="true" aria-labelledby="cust-exit-title">
        <h3 id="cust-exit-title">Unsaved theme changes</h3>
        <p class="sub">Your latest edits haven’t been saved yet.</p>
        <div class="row">
          <button type="button" class="btn btn-danger btn-sm" id="cust-exit-discard">Exit without saving</button>
          <button type="button" class="btn btn-cta btn-sm" id="cust-exit-save">Save and exit</button>
          <button type="button" class="btn btn-ghost btn-sm" id="cust-exit-cancel">Keep editing</button>
        </div>
      </div>
    </div>`}
  </div>`, inline ? undefined : true);
  if (!inline) m.classList.add("customize");

  function isDirty(){
    return JSON.stringify(bdCanonicalPrefs(draft)) !== JSON.stringify(savedPrefs);
  }

  function previewDraft(){ bdPaintThemePrefs(JSON.parse(JSON.stringify(draft))); }

  async function persistTheme(){
    bdPaintThemePrefs(JSON.parse(JSON.stringify(draft)));
    savedPrefs = JSON.parse(JSON.stringify(bdCanonicalPrefs(draft)));
    account = await bdUpsertActiveAccountTheme(api, bdAlignPrefsMode(draft), account);
    /* Cache only after hosted write succeeds (upsert throws otherwise). */
    bdCacheThemePrefs(bdAlignPrefsMode(draft));
    return bdAlignPrefsMode(draft);
  }

  function presetMini(t){
    /* an accurate miniature of the real dashboard, painted in this theme's
       own tokens: tinted page, sidebar rail, floating pill, stat cards, CTA */
    const card = (grow) => `<span style="flex:${grow||1};border-radius:3px;background:${t.card};box-shadow:inset 0 0 0 1px ${t["line-soft"]}"></span>`;
    const navItem = (on) => `<i style="display:block;height:7px;border-radius:3px;margin:0 6px 4px;background:${on?t.brand:t["line-soft"]};${on?"":"opacity:.7"}"></i>`;
    return `<span class="bd-mini" aria-hidden="true" style="display:flex;width:100%;height:100%;border-radius:9px;overflow:hidden;background:${t.bg}">
      <span style="flex:0 0 34%;display:flex;flex-direction:column;padding:7px 0;background:${t["bg-soft"]};border-right:1px solid ${t["line-soft"]}">
        <i style="height:8px;border-radius:3px;margin:0 6px 8px;background:${t.ink};opacity:.78"></i>
        ${navItem(true)}${navItem(false)}${navItem(false)}${navItem(false)}
      </span>
      <span style="flex:1;display:flex;flex-direction:column;gap:5px;padding:7px">
        <span style="display:flex;align-items:center;gap:4px;height:13px;border-radius:999px;padding:0 6px;background:${t["bg-alt"]};box-shadow:0 1px 3px rgba(0,0,0,.10),inset 0 0 0 1px ${t["line-soft"]}">
          <i style="width:5px;height:5px;border-radius:50%;background:${t.brand}"></i>
          <i style="flex:1;height:4px;border-radius:2px;background:${t["line-soft"]}"></i>
        </span>
        <span style="display:flex;gap:4px;height:22px">${card()}${card()}${card()}</span>
        <span style="display:flex;gap:4px;flex:1">
          ${card(2)}
          <span style="flex:1;border-radius:3px;background:${t.card};box-shadow:inset 0 0 0 1px ${t["line-soft"]};display:flex;flex-direction:column;justify-content:flex-end;padding:4px">
            <i style="height:9px;border-radius:3px;background:${t.cta}"></i>
          </span>
        </span>
      </span>
    </span>`;
  }

  function presetCard(id){
    const p = BD_THEME_PRESETS[id];
    const t = p[p.mode === "dark" ? "dark" : "light"] || p.light;
    const sel = draft.preset === id;
    const mark = sel ? `<span class="bd-swatch-check" aria-hidden="true">${lu("check-circle","lu16")}</span>` : "";
    return `<button type="button" class="cust-preset bd-theme-swatch${sel ? " sel" : ""}" data-preset="${id}" aria-pressed="${sel}">
      <span class="bd-swatch-preview" style="--swatch-accent:${t.brand};--swatch-bg:${t.bg}" aria-hidden="true">${presetMini(t)}</span>
      ${mark}
      <span class="cust-preset-name">${esc(p.name)}</span>
      <span class="cust-preset-tag">${esc(p.tag)}</span>
    </button>`;
  }

  function colorRow(key){
    const val = (draft.custom && draft.custom[key]) || bdPresetTokens(draft.preset, draft.mode)[key];
    const on = focusToken === key;
    return `<div class="cust-color${on ? " on" : ""}" data-token="${key}">
      <button type="button" class="cust-swatch" style="background:${val}" data-pick="${key}" aria-label="Edit ${esc(BD_TOKEN_LABELS[key])}"></button>
      <div class="cust-color-meta">
        <strong>${esc(BD_TOKEN_LABELS[key])}</strong>
        <input type="text" class="cust-hex" data-hex="${key}" value="${esc(val)}" spellcheck="false">
      </div>
      <button type="button" class="cust-reset" data-reset="${key}" title="Reset">${lu("rotate-ccw","lu14")}</button>
    </div>`;
  }

  function previewHtml(){
    return `<div class="cust-adv-right">
      <p class="cust-preview-kicker">Element preview</p>
      <p class="cust-preview-label" id="cust-preview-hint">Showing: ${esc(BD_TOKEN_LABELS[focusToken])}</p>
      <div class="cust-preview" id="cust-preview" data-pr="bg">
        <div class="cust-pr-top" data-pr="panel line-soft"><span>Preview</span></div>
        <div class="cust-pr-stat" data-pr="bg-alt line-soft"><span></span><span></span></div>
        <div class="cust-pr-inner">
          <div class="cust-pr-side" data-pr="bg-soft line-soft"></div>
          <div class="cust-pr-card" data-pr="card line">
            <div class="cust-pr-title" data-pr="ink">Card title</div>
            <div class="cust-pr-body" data-pr="muted">Body text on your dashboard surfaces.</div>
            <div class="cust-pr-muted" data-pr="subtle">Muted secondary line</div>
            <div class="cust-pr-field" data-pr="field line">Sample input</div>
            <div class="cust-pr-divider" data-pr="line-soft"></div>
            <div class="cust-pr-actions">
              <span class="cust-pr-cta" data-pr="cta">Primary action</span>
              <span class="cust-pr-brand" data-pr="brand"></span>
              <span class="cust-pr-pill" data-pr="brand-mid">Tag</span>
              <span class="cust-pr-link" data-pr="brand-hover">Link</span>
              <span class="cust-pr-danger" data-pr="bad">Discard</span>
            </div>
          </div>
        </div>
      </div>
    </div>`;
  }

  function accountHtml(){
    const rows = account.saved.length ? account.saved.map(t => {
      const active = account.active === t.id;
      const preset = bdNormalizePresetId(t.preset);
      const tokens = t.custom || (BD_THEME_PRESETS[preset] || BD_THEME_PRESETS.blueprint)[t.mode === "dark" ? "dark" : "light"];
      const chips = `<span class="cust-acct-swatch" aria-hidden="true"><i style="background:${tokens.brand}"></i><i style="background:${tokens.bg}"></i><i style="background:${tokens["bg-soft"]}"></i></span>`;
      return `<div class="cust-acct-row${active ? " active" : ""}">
        <div class="cust-acct-meta">${chips}<strong>${esc(t.name)}</strong>${active ? `<em>Active on your account</em>` : `<em>${esc(BD_THEME_PRESETS[preset]?.name || "Custom")} · ${t.mode}</em>`}</div>
        <div class="cust-acct-actions">
          ${active ? `<button type="button" class="btn btn-ghost btn-sm" data-acct-clear="${t.id}">Clear active</button>` : `<button type="button" class="btn btn-ghost btn-sm" data-acct-apply="${t.id}">Use</button>`}
          <button type="button" class="cust-acct-del" data-acct-del="${t.id}">Delete</button>
        </div>
      </div>`;
    }).join("") : `<p class="cust-acct-empty">No saved account themes yet — name your current look and press Save to account.</p>`;
    return `<div class="cust-section cust-account">
      <h3>Account themes</h3>
      <p class="c-sub">Save extra palettes to your account, switch which one is active on every device, or use Save theme below to update your current edits.</p>
      <label class="cust-acct-label" for="cust-theme-name">New theme name</label>
      <div class="cust-acct-new">
        <input type="text" id="cust-theme-name" class="cust-acct-input" placeholder="My theme" maxlength="48">
        <button type="button" class="btn btn-dark btn-sm" id="cust-save-account">Save to account</button>
      </div>
      <div class="cust-acct-list">${rows}</div>
    </div>`;
  }

  function updatePreviewHint(token){
    const hint = m.querySelector("#cust-preview-hint");
    if (hint) hint.textContent = "Showing: " + (BD_TOKEN_LABELS[token] || token);
    bdPaintThemePreview(m.querySelector("#cust-preview"), bdResolveThemeTokens(draft), token);
  }

  function paint(){
    const body = m.querySelector("#cust-body");
    Object.assign(draft, bdAlignPrefsMode(draft));
    const presets = `<div class="cust-section">
      <div class="cust-section-head"><div><h3>Appearance</h3><p class="c-sub">Light or Dark — then fine-tune colours below if you want. Saved themes follow your account on every device.</p></div>
        <button type="button" class="btn btn-ghost btn-sm cust-head-btn" id="cust-random">${lu("shuffle","lu14")} Swap</button></div>
      <div class="cust-preset-grid cust-preset-grid--duo">${Object.keys(BD_THEME_PRESETS).map(presetCard).join("")}</div>
    </div>`;
    const advHead = `<div class="cust-section cust-adv-head">
      <div><h3>Your theme</h3><p class="c-sub">Hover or select a colour to highlight the matching element in the preview.</p></div>
      <label class="cust-toggle"><span>Advanced mode</span><input type="checkbox" id="cust-advanced"${advanced ? " checked" : ""}><i class="cust-switch"></i></label>
    </div>`;
    const advBody = advanced ? `<div class="cust-adv">
      <div class="cust-adv-left">
        <div class="cust-harmony"><span>Suggested harmony</span><div class="cust-harmony-chips" id="cust-harmony"></div></div>
        <input type="search" class="cust-search" id="cust-search" placeholder="Search elements…" value="${esc(search)}">
        <div class="cust-colors" id="cust-colors">${filteredColorKeys().map(colorRow).join("")}</div>
      </div>
      ${previewHtml()}
    </div>` : "";
    body.innerHTML = presets + advHead + advBody + accountHtml();
    updatePreviewHint(focusToken);
    const harmony = m.querySelector("#cust-harmony");
    if (harmony){
      const brand = (draft.custom && draft.custom.brand) || bdPresetTokens(draft.preset, draft.mode).brand;
      harmony.innerHTML = bdHarmonyColors(brand).map(c =>
        `<button type="button" style="background:${c}" data-harm="${c}" aria-label="Apply ${esc(c)}"></button>`).join("");
    }
    icons();
    bindColorList();
    bindBody();
  }

  function filteredColorKeys(){
    return BD_THEME_VAR_KEYS.filter(k => !search || BD_TOKEN_LABELS[k].toLowerCase().includes(search.toLowerCase()));
  }

  function paintColorList(){
    const list = m.querySelector("#cust-colors");
    if (!list) return;
    list.innerHTML = filteredColorKeys().map(colorRow).join("");
    icons();
    bindColorList();
  }

  function bindColorList(){
    m.querySelectorAll(".cust-color").forEach(row => {
      row.onmouseenter = () => updatePreviewHint(row.dataset.token);
      row.onmouseleave = () => updatePreviewHint(focusToken);
      row.onclick = () => {
        focusToken = row.dataset.token;
        m.querySelectorAll(".cust-color").forEach(r => r.classList.toggle("on", r.dataset.token === focusToken));
        updatePreviewHint(focusToken);
      };
    });
    m.querySelectorAll(".cust-swatch").forEach(btn => {
      btn.onclick = e => {
        e.stopPropagation();
        const token = btn.dataset.pick;
        focusToken = token;
        document.querySelectorAll(".cust-color-pop-host").forEach(h => h.remove());
        const cur = (draft.custom && draft.custom[token]) || bdPresetTokens(draft.preset, draft.mode)[token];
        const host = document.createElement("div");
        host.className = "cust-color-pop-host";
        const pop = bdColorPopover(
          cur,
          v => {
            setToken(token, v);
            btn.style.background = v;
            const hexInp = m.querySelector(`.cust-hex[data-hex="${token}"]`);
            if (hexInp) hexInp.value = v.toUpperCase();
          },
          v => { setToken(token, v); host.remove(); }
        );
        host.appendChild(pop);
        document.body.appendChild(host);
        const r = btn.getBoundingClientRect();
        const left = Math.min(Math.max(12, r.left), window.innerWidth - 268);
        const top = Math.min(r.bottom + 8, window.innerHeight - 320);
        host.style.cssText = `position:fixed;z-index:600;left:${left}px;top:${top}px`;
        const close = ev => {
          if (!host.contains(ev.target) && !btn.contains(ev.target)){
            host.remove();
            document.removeEventListener("mousedown", close, true);
          }
        };
        setTimeout(() => document.addEventListener("mousedown", close, true), 0);
      };
    });
    m.querySelectorAll(".cust-hex").forEach(inp => {
      inp.onclick = e => e.stopPropagation();
      inp.onchange = () => {
        const key = inp.dataset.hex;
        const v = inp.value.trim();
        if (/^#[0-9a-fA-F]{6}$/.test(v)) setToken(key, v);
        else paintColorList();
      };
    });
    m.querySelectorAll("[data-reset]").forEach(btn => {
      btn.onclick = e => {
        e.stopPropagation();
        const key = btn.dataset.reset;
        if (!draft.custom) return;
        delete draft.custom[key];
        if (!Object.keys(draft.custom).length) draft.custom = null;
        previewDraft();
        paint();
      };
    });
  }

  async function persistAccount(next){
    account = await bdSyncAccountThemes(api, next);
  }

  function closeCustomize(revert){
    if (typeof clearModalCloseHookFn === "function") clearModalCloseHookFn();
    if (revert) bdApplyThemePrefs(JSON.parse(JSON.stringify(savedPrefs)));
    closeModalFn();
  }

  function hideExitPrompt(){ const el = m.querySelector("#cust-exit-prompt"); if (el) el.hidden = true; }

  function requestCloseCustomize(){
    const prompt = m.querySelector("#cust-exit-prompt");
    if (prompt && !prompt.hidden){
      hideExitPrompt();
      return;
    }
    if (!isDirty()) return closeCustomize(false);
    if (prompt) prompt.hidden = false;
    m.querySelector("#cust-exit-cancel")?.focus();
  }

  if (!inline && typeof setModalCloseHookFn === "function") {
    setModalCloseHookFn(() => {
      requestCloseCustomize();
      return false;
    });
  }

  function bindChrome(){
    m.querySelector("#cust-close")?.addEventListener("click", () => requestCloseCustomize());
    m.querySelector("#cust-exit-cancel")?.addEventListener("click", hideExitPrompt);
    m.querySelector("#cust-exit-discard")?.addEventListener("click", () => { hideExitPrompt(); closeCustomize(true); });
    m.querySelector("#cust-exit-save")?.addEventListener("click", async () => {
      hideExitPrompt();
      const btn = m.querySelector("#cust-exit-save");
      if (btn) btn.disabled = true;
      try {
        await persistTheme();
        closeCustomize(false);
      } catch {
        toast("Could not save your theme.");
        if (btn) btn.disabled = false;
      }
    });
    const ex = m.querySelector("#cust-export");
    if (ex) ex.onclick = () => {
      const blob = new Blob([JSON.stringify(bdCanonicalPrefs(draft), null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "bd-theme.json";
      a.click();
      URL.revokeObjectURL(a.href);
      toast("Theme exported");
    };
    const im = m.querySelector("#cust-import");
    if (im) im.onclick = () => {
      const inp = document.createElement("input");
      inp.type = "file";
      inp.accept = "application/json,.json";
      inp.onchange = () => {
        const f = inp.files?.[0]; if (!f) return;
        const r = new FileReader();
        r.onload = () => {
          try {
            const parsed = JSON.parse(String(r.result || ""));
            draft = bdCanonicalPrefs(Object.assign(bdAlignPrefsMode(bdLoadThemePrefs()), parsed));
            previewDraft();
            toast("Theme imported"); paint();
          } catch { toast("Could not parse theme file."); }
        };
        r.readAsText(f);
      };
      inp.click();
    };
    m.querySelector("#cust-save").onclick = async () => {
      const btn = m.querySelector("#cust-save");
      const saved = m.querySelector("#cust-saved");
      btn.disabled = true;
      try {
        await persistTheme();
        if (saved){ saved.hidden = false; icons(); }
        btn.textContent = "Saved";
        if (inline) {
          setTimeout(() => { btn.textContent = "Save theme"; btn.disabled = false; }, 1400);
        } else {
          setTimeout(() => closeCustomize(false), 720);
        }
      } catch {
        toast("Could not save your theme.");
        btn.disabled = false;
      }
    };
  }

  function bindBody(){
    m.querySelector("#cust-random").onclick = () => {
      draft = bdRandomThemePrefs();
      previewDraft();
      paint();
    };
    m.querySelectorAll("[data-preset]").forEach(b => b.onclick = () => {
      draft.preset = b.dataset.preset;
      draft.mode = BD_THEME_PRESETS[bdNormalizePresetId(draft.preset)]?.mode || "light";
      draft.custom = null;
      previewDraft();
      paint();
    });
    m.querySelectorAll("[data-mode]").forEach(b => b.onclick = () => {
      draft.mode = b.dataset.mode;
      draft.custom = null;
      previewDraft();
      paint();
    });
    const adv = m.querySelector("#cust-advanced");
    if (adv) adv.onchange = () => { advanced = adv.checked; paint(); };
    const si = m.querySelector("#cust-search");
    if (si) si.oninput = () => { search = si.value; paintColorList(); };
    m.querySelectorAll("[data-harm]").forEach(btn => {
      btn.onclick = () => setToken("brand", btn.dataset.harm);
    });

    const saveAcct = m.querySelector("#cust-save-account");
    if (saveAcct) saveAcct.onclick = async () => {
      const name = (m.querySelector("#cust-theme-name")?.value || "").trim();
      if (!name) return toast("Enter a theme name first.");
      const snap = bdSnapshotPrefs(draft);
      const entry = Object.assign({ id: crypto.randomUUID(), name, updated_at: new Date().toISOString() }, snap);
      account.saved.unshift(entry);
      account.active = entry.id;
      await persistTheme();
      await persistAccount(account);
      toast(`“${name}” saved to your account`);
      paint();
    };
    m.querySelectorAll("[data-acct-apply]").forEach(b => b.onclick = async () => {
      const t = account.saved.find(x => x.id === b.dataset.acctApply);
      if (!t) return;
      account.active = t.id;
      draft = bdCanonicalPrefs(t);
      bdApplyThemePrefs(bdAlignPrefsMode(draft));
      savedPrefs = JSON.parse(JSON.stringify(bdCanonicalPrefs(draft)));
      await persistAccount(account);
      toast(`“${t.name}” is now active on your account`);
      paint();
    });
    m.querySelectorAll("[data-acct-clear]").forEach(b => b.onclick = async () => {
      account.active = null;
      await persistAccount(account);
      toast("Cleared active account theme");
      paint();
    });
    m.querySelectorAll("[data-acct-del]").forEach(b => b.onclick = async () => {
      const id = b.dataset.acctDel;
      account.saved = account.saved.filter(t => t.id !== id);
      if (account.active === id) account.active = null;
      await persistAccount(account);
      toast("Theme deleted");
      paint();
    });
  }

  function setToken(key, value){
    if (!draft.custom) draft.custom = {};
    const base = bdPresetTokens(draft.preset, draft.mode)[key];
    if (value.toLowerCase() === String(base).toLowerCase()){
      delete draft.custom[key];
      if (!Object.keys(draft.custom).length) draft.custom = null;
    } else draft.custom[key] = value;
    focusToken = key;
    previewDraft();
    paint();
  }

  previewDraft();
  paint();
  savedPrefs = JSON.parse(JSON.stringify(bdCanonicalPrefs(draft)));
  bindChrome();
  bdFetchAccountThemes(api).then(acct => { account = acct; paint(); });
}

/** Mount customize UI inline (e.g. My account customization tab). */
function bdMountCustomizePanel(mountEl, ui) {
  if (!mountEl) return;
  bdOpenCustomizeModal(Object.assign({}, ui, { mount: mountEl }));
}
